import React, { useState, useRef } from "react"
import styled, { css } from "styled-components"
import { useStaticQuery, graphql } from "gatsby"
import { useIntl } from "gatsby-plugin-react-intl"
import { useClickAway, useKeyPressEvent } from "react-use"

import StyledLink from "./Link"

import { media } from "../styles"
import BackSVG from "../images/icons/back--small.inline.svg"
import ExternalSVG from "../images/icons/external.inline.svg"
import StyledNavigationGrid, { SubNavStyles } from "./NavigationGrid"
import NavigationIconGrid from "./NavigationIconGrid"

const menuStyles = css`
  font-size: var(--font-size-15);
  font-weight: 700;
  letter-spacing: -0.07px;
`

const menuServicesOffsetLg = "22.7%"
const menuServicesOffsetXl = "19.8%"

const NavContainer = styled.nav`
  position: relative;
  z-index: 99999;
  display: none;

  @media ${media.lg} {
    display: block;
  }

  @media print {
    display: none;
  }
`

const Flyout = styled.div`
  left: 50%;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  top: calc(100% + 25px);
  transform-origin: top center;
  transform: translate3d(-50%, -10px, 0) scale(0.94);
  transition:
    opacity var(--transitionTime),
    transform var(--transitionTime);
  z-index: 1;
  display: inline;

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }

  ${(props) =>
    props.$isOpen &&
    css`
      opacity: 1;
      pointer-events: auto;
      transform: translate3d(-50%, 0, 0) scale(1);
    `}

  ${(props) =>
    props.$mode === "grid" &&
    css`
      transform: ${`translate3d(-${menuServicesOffsetLg}, -10px, 0) scale(0.94)`};
      transform-origin: ${menuServicesOffsetLg} 0;

      ${(props) =>
        props.$isOpen &&
        css`
          pointer-events: auto;
          transform: ${`translate3d(-${menuServicesOffsetLg}, 0, 0) scale(1)`};
        `}

      @media ${media.xl} {
        transform: ${`translate3d(-${menuServicesOffsetXl}, -10px, 0) scale(0.94)`};
        transform-origin: ${menuServicesOffsetXl} 0;

        ${(props) =>
          props.$isOpen &&
          css`
            pointer-events: auto;
            transform: ${`translate3d(-${menuServicesOffsetXl}, 0, 0) scale(1)`};
          `}
      }
    `}
`

const Menu = styled.ul`
  ${menuStyles}
  display: flex;
  flex-flow: row nowrap;
  gap: 10px;
  list-style: none;
  margin: 0;
  padding: 0;

  @media ${media.xl} {
    gap: 20px;
  }

  a,
  button {
    align-items: center;
    color: inherit;
    display: flex;
    font-size: inherit;
    font-weight: inherit;
    text-decoration: none;
    transition: color var(--transitionTime);

    @media (prefers-reduced-motion: reduce) {
      transition: none;
    }

    svg {
      flex: none;
    }

    svg:first-child {
      margin-right: 11px;
    }

    svg:last-child {
      margin-left: 11px;
    }

    &:hover {
      color: var(--color-sage);
    }
  }

  > li > a,
  > li > button {
    padding: 5px;
  }
`

const MenuItem = styled.li`
  margin: 0;
  padding: 0;
  position: relative;

  &:last-child {
    border: none;
  }
`

const Link = styled(StyledLink)`
  ul ul &.current {
    color: var(--color-sun);
    font-weight: 700;
  }
`

const SubmenuToggle = styled.button`
  appearance: none;
  background: transparent;
  border: none;
  cursor: default;
  line-height: inherit;
  margin: 0;
  padding: 0;

  &:hover {
    color: var(--color-sun);
  }

  ${(props) =>
    props.$isOpen &&
    css`
      color: var(--color-sage) !important;
    `}
`

const SubMenu = styled.ul`
  ${SubNavStyles}
  display: flex;
  flex-flow: column nowrap;
  list-style: none;
  margin: 0;
  padding: 0;

  ${(props) =>
    props.$isOpen &&
    css`
      color: var(--color-sage) !important;
    `}

  ${MenuItem} {
    padding: 3px 0;
  }

  a,
  button {
    padding: 2px 0;

    &:hover {
      color: var(--color-sun);
    }
  }
`

const SubContainer = styled.div`
  background: var(--color-white);
  border-radius: 18px;
  box-shadow: 0 0 45px rgba(var(--color-spaceblueRGB), 0.1);
  padding: 30px;
  position: relative;
  min-width: 215px;

  &::before {
    border: 20px solid transparent;
    border-bottom-color: #fff;
    content: "";
    display: block;
    height: 0;
    left: 50%;
    margin: 0 auto;
    pointer-events: none;
    position: absolute;
    top: -40px;
    transform: translateX(-50%);
    width: 0;

    ${(props) =>
      props.$mode === "grid" &&
      css`
        left: ${menuServicesOffsetLg};

        @media ${media.xl} {
          left: ${menuServicesOffsetXl};
        }
      `}
  }
`

const NavigationGrid = styled(StyledNavigationGrid)`
  max-width: 100%;

  @media ${media.lg} {
    width: 800px;
  }

  @media ${media.xl} {
    width: 800px;
  }
`

const SubNav = ({ id, index, isOpen, mode, children, onClickAway }) => {
  const ref = useRef(null)

  useClickAway(ref, () => {
    isOpen && onClickAway()
  })

  return (
    <Flyout
      aria-labelledby={`desktop-menu-item-${index}`}
      id={id}
      $isOpen={isOpen}
      $mode={mode}
      onMouseLeave={() => onClickAway()}
      ref={ref}
    >
      {mode === "grid" ? (
        <SubContainer $mode={mode}>{children}</SubContainer>
      ) : (
        <SubContainer $mode={mode}>
          <SubMenu>{children}</SubMenu>
        </SubContainer>
      )}
    </Flyout>
  )
}

const HeaderNavigationDesktop = (props) => {
  const [subOpen, setSubOpen] = useState()
  const intl = useIntl()

  useKeyPressEvent("Escape", () => {
    if (subOpen) {
      toggleSubMenu({ path: null })
    }
  })

  const close = () => {
    setSubOpen()
  }

  const toggleSubMenu = ({ path }) => {
    setSubOpen(path || null)
  }

  const data = useStaticQuery(graphql`
    query HeaderNavigationDesktopQuery {
      allNavigationJson {
        nodes {
          locale
          items {
            label
            slug
            mobileOnly
            url
            mode
            items {
              label
              slug
            }
          }
        }
      }
      allProductCollections: allContentfulProductCollection(
        filter: { visible: { eq: true }, isOld: { ne: true } }
        sort: { sortOrder: ASC }
      ) {
        nodes {
          id
          node_locale
          icon: iconSmall {
            ...ContentfulImageData
          }
          name
          items: products {
            ... on ContentfulProduct {
              name
              slug
            }
          }
        }
      }
      allIndustries: allContentfulIndustry(
        filter: {
          hasIndustryPage: { eq: true }
          hideIndustryPageFromNavigation: { ne: true }
        }
        sort: { sortOrder: ASC }
      ) {
        nodes {
          id
          name
          node_locale
          slug
          icon: iconSmall {
            ...ContentfulImageData
          }
        }
      }
    }
  `)

  const navItems = data.allNavigationJson.nodes
    .find((node) => node.locale === intl.locale)
    ?.items?.filter((navItem) => navItem.mobileOnly !== "true")

  const productCollections = data.allProductCollections.nodes.filter(
    (node) => node.node_locale === intl.locale
  )

  const industries = data.allIndustries.nodes.filter(
    (node) => node.node_locale === intl.locale
  )

  const renderMenuItem = ({
    index,
    isFirst,
    isLast,
    isToplevel,
    item,
    parent,
    prefix,
    subOpen,
  }) => {
    const handleBlur = (event) => {
      if (event.relatedTarget?.attributes["data-close-menu"]) {
        // The blur event was caused by a keyboard event
        toggleSubMenu({ path: null })
      }
    }
    if (item.items?.length > 0) {
      return (
        <SubmenuToggle
          aria-controls={`desktop-submenu-${index}`}
          aria-haspopup="true"
          id={`desktop-menu-item-${index}`}
          $isOpen={subOpen === item.slug}
          onBlur={isFirst || isLast ? handleBlur : null}
          onClick={() => toggleSubMenu({ path: item.slug })}
          onFocus={() => toggleSubMenu({ path: item.slug, isFirst, isLast })}
          onMouseOver={() => toggleSubMenu({ path: item.slug })}
        >
          {item.label}
        </SubmenuToggle>
      )
    } else if (item.slug) {
      const slug = prefix ? `${prefix}/${item.slug}/` : item.slug
      return (
        <Link
          intl={item.intl === false}
          onBlur={isFirst || isLast ? handleBlur : null}
          onFocus={
            isToplevel
              ? () => toggleSubMenu({ path: null })
              : () => toggleSubMenu({ path: parent || null })
          }
          onMouseOver={isToplevel ? () => toggleSubMenu({ path: null }) : null}
          to={slug}
        >
          {item.label}
        </Link>
      )
    } else if (item.url) {
      return (
        <a
          href={item.url}
          target="_blank"
          onBlur={isFirst || isLast ? handleBlur : null}
          onFocus={
            isToplevel
              ? () => toggleSubMenu({ path: null })
              : () => toggleSubMenu({ path: parent || null })
          }
          onMouseOver={isToplevel ? () => toggleSubMenu({ path: null }) : null}
          rel="noopener noreferrer"
        >
          {item.icon === "back" && <BackSVG />} <span>{item.label}</span>{" "}
          {item.icon === "ext" && <ExternalSVG />}
        </a>
      )
    } else {
      return item.label
    }
  }

  return (
    <NavContainer
      {...props}
      aria-label={intl.formatMessage({
        id: "accessibility.regions.mainNavigation",
      })}
      role="navigation"
    >
      <Menu>
        {navItems.map((navItem, topIndex) => (
          <MenuItem key={navItem.label}>
            {renderMenuItem({
              item: navItem,
              subOpen,
              isToplevel: true,
              isFirst: topIndex === 0,
              isLast: topIndex === navItems.length - 1,
              index: topIndex,
            })}
            {navItem.items && (
              <SubNav
                id={`desktop-submenu-${topIndex}`}
                index={topIndex}
                isOpen={subOpen === navItem.slug}
                mode={navItem.mode}
                onClickAway={() => close()}
              >
                {navItem.items.map((subItem, subIndex) => {
                  switch (subItem.label) {
                    case "RENDER_SERVICES":
                      return (
                        <NavigationGrid
                          basePath="/services/"
                          headerLevel={2}
                          iconColor="sun"
                          items={productCollections}
                          key={subItem.label}
                          mode="topnav"
                          handleFocus={() =>
                            toggleSubMenu({ path: navItem.slug })
                          }
                        />
                      )
                    case "RENDER_INDUSTRIES":
                      return (
                        <NavigationIconGrid
                          basePath="/industries/"
                          iconColor="sun"
                          items={industries}
                          key={subItem.label}
                          mode="topnav"
                          handleFocus={() =>
                            toggleSubMenu({ path: navItem.slug })
                          }
                        />
                      )
                    default:
                      return (
                        <MenuItem key={subItem.label}>
                          {renderMenuItem({
                            item: subItem,
                            subOpen,
                            parent: navItem.slug,
                            isLast:
                              topIndex === navItems.length - 1 &&
                              subIndex === navItem.items.length - 1,
                          })}
                        </MenuItem>
                      )
                  }
                })}
              </SubNav>
            )}
          </MenuItem>
        ))}
      </Menu>

      {/* {subOpen && <Overlay onClick={() => close()} />} */}
    </NavContainer>
  )
}

export default HeaderNavigationDesktop
